import * as Sentry from '@sentry/nextjs';
import { capitalize } from 'lodash';
import { type GetStaticPaths, type GetStaticProps } from 'next';
import {
  FREE_COURT_SLUG,
  INDOOR_COURT_SLUG,
  OUTDOOR_COURT_SLUG,
  getCourtPageUrl,
} from 'constants/pages';
import { MILES_NEARYBY_COURTS, MILES_NEARYBY_LESSONS, NEARBY_COURT_LIMIT } from 'constants/seo';
import {
  CoachAvailabilityStatusesEnum,
  type GetCityClinicsQuery,
  type GetCityCoachesQuery,
  type GetTournamentsForStateQuery,
  SportsEnum,
} from 'types/generated/server';
import { getImageUrl } from 'services/client/cloudflare/getImageUrl';
import { getAllCityVenuePaths } from 'services/server/graphql/queries/getAllCityVenuePaths';
import { getCityClinics } from 'services/server/graphql/queries/getCityClinics';
import { getCityCoaches } from 'services/server/graphql/queries/getCityCoaches';
import { getTennisCityPageContentByCitySlug } from 'services/server/graphql/queries/getCityPageContentByCitySlug';
import { getCityTennisVenuesByCitySlug } from 'services/server/graphql/queries/getCityVenuesByCitySlug';
import { getTennisVenuesByGeo } from 'services/server/graphql/queries/getVenuesByGeo';
import { getTournamentsForState } from 'services/server/graphql/queries/getTournamentsForState';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { milesToMeters } from 'utils/shared/geo/milesToMeters';
import { generateBreadcrumbListJsonLd } from 'utils/shared/json-ld/breadcrumbs';
import { combineJsonLdSchemas } from 'utils/shared/json-ld/combineJsonLdSchemas';
import { generateFaqJsonLd } from 'utils/shared/json-ld/faq';
import { generateItemListJsonLd } from 'utils/shared/json-ld/itemList';
import { generatePlaceJsonLd } from 'utils/shared/json-ld/place';
import { generateSportsActivityLocationJsonLd } from 'utils/shared/json-ld/sports';
import { generateSportsEventJsonLd } from 'utils/shared/json-ld/sportsEvent';
import CityVenuesPage, {
  Props,
  generateCourtsBreadcrumbs,
  generateCourtsMeta,
  getTitleLocationName,
} from 'screens/CityVenuesPage';
import { TagLinkKey } from 'screens/CityVenuesPage/types';
import {
  filterVenuesForTagFree,
  filterVenuesForTagIndoor,
  filterVenuesForTagOutdoor,
} from 'screens/CityVenuesPage/utils';
import { generateFaqs } from 'screens/CityDetailPage/utils';
import CityLocationProvider from 'components/utilities/CityLocationProvider';

const REVALIDATE_IN_SECONDS = 6 * 60 * 60 * 1;
const IS_ALL_AS_ISR = process.env.APP_STAGE !== 'production';

const resolveOptionalQuery = async <T,>(query: Promise<T>): Promise<T | null> => {
  try {
    return await query;
  } catch (error) {
    Sentry.captureException(error);
    return null;
  }
};

export const getStaticProps: GetStaticProps<Props, { citySlug: string }> = async ({ params }) => {
  const citySlug = params?.citySlug;

  if (!citySlug) {
    return {
      notFound: true,
      revalidate: REVALIDATE_IN_SECONDS,
    };
  }

  try {
    const cityVenueResponse = await getCityTennisVenuesByCitySlug({ slug: citySlug });
    const city = cityVenueResponse?.cities[0];

    if (!city || !city?.id || !city?.name) {
      return {
        notFound: true,
        revalidate: REVALIDATE_IN_SECONDS,
      };
    }

    // Fetch city content for coaches, tournaments, groups, etc.
    const contentResp = await resolveOptionalQuery(
      getTennisCityPageContentByCitySlug({
        slug: citySlug,
        limit: 7,
      }),
    );
    const cityContent = contentResp?.cities?.[0];

    const nearbyVenuesResponse = await resolveOptionalQuery(
      getTennisVenuesByGeo({
        distance: milesToMeters(MILES_NEARYBY_COURTS),
        from: {
          type: 'Point',
          coordinates: [city.longitude, city.latitude],
        },
        limit: NEARBY_COURT_LIMIT,
      }),
    );

    // Fetch state tournaments
    let stateTournaments: GetTournamentsForStateQuery['events'] = [];
    if (city?.countrySubdivision?.id) {
      const stateResp = await resolveOptionalQuery(
        getTournamentsForState({
          stateId: city?.countrySubdivision?.id || '',
        }),
      );
      stateTournaments = stateResp?.events || [];
    }

    // Fetch top coaches
    let topCoaches: GetCityCoachesQuery['userProfiles'] = [];
    const searchRadius = milesToMeters(MILES_NEARYBY_LESSONS);
    if (city?.id && city?.latitude && city?.longitude) {
      const topCoachesResp = await resolveOptionalQuery(
        getCityCoaches({
          distance: searchRadius,
          from: {
            type: 'Point',
            coordinates: [city.longitude, city.latitude],
          },
          sport: SportsEnum.Tennis,
          defaultSport: SportsEnum.Tennis as string,
          limit: 50,
        }),
      );
      const allCoaches = topCoachesResp?.userProfiles || [];
      topCoaches = allCoaches
        .sort((a, b) => {
          const aCanInstantBook = a.coachAvailability?.some(
            (avail) => avail.status === CoachAvailabilityStatusesEnum.Active,
          )
            ? 1
            : 0;
          const bCanInstantBook = b.coachAvailability?.some(
            (avail) => avail.status === CoachAvailabilityStatusesEnum.Active,
          )
            ? 1
            : 0;
          if (aCanInstantBook !== bCanInstantBook) return bCanInstantBook - aCanInstantBook;
          const aHasReviews = (a.coachLessonsAggregate?.aggregate?.count || 0) > 0 ? 1 : 0;
          const bHasReviews = (b.coachLessonsAggregate?.aggregate?.count || 0) > 0 ? 1 : 0;
          if (aHasReviews !== bHasReviews) return bHasReviews - aHasReviews;
          const aHasPhoto = a.profileImagePath ? 1 : 0;
          const bHasPhoto = b.profileImagePath ? 1 : 0;
          if (aHasPhoto !== bHasPhoto) return bHasPhoto - aHasPhoto;
          return (b.followersAggregate?.aggregate?.count || 0) - (a.followersAggregate?.aggregate?.count || 0);
        })
        .slice(0, 8);
    }

    // Fetch top clinics
    let topClinics: GetCityClinicsQuery['lessons'] = [];
    if (city?.id && city?.latitude && city?.longitude) {
      const startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 30);
      endDate.setHours(23, 59, 59, 999);

      const topClinicsResp = await resolveOptionalQuery(
        getCityClinics({
          distance: searchRadius,
          from: {
            type: 'Point',
            coordinates: [city.longitude, city.latitude],
          },
          sport: SportsEnum.Tennis,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          limit: 50,
        }),
      );
      const allClinics = topClinicsResp?.lessons || [];
      topClinics = allClinics
        .sort((a, b) => {
          const aRating = a.ownerProfile?.coachReviewsAggregate?.aggregate?.avg?.rating || 0;
          const bRating = b.ownerProfile?.coachReviewsAggregate?.aggregate?.avg?.rating || 0;
          if (aRating !== bRating) return bRating - aRating;
          const aParticipants = a.participants?.length || 0;
          const bParticipants = b.participants?.length || 0;
          if (aParticipants !== bParticipants) return bParticipants - aParticipants;
          const aPrice = a.priceUnitAmount || 0;
          const bPrice = b.priceUnitAmount || 0;
          if (aPrice !== bPrice) return aPrice - bPrice;
          return new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime();
        })
        .slice(0, 8);
    }

    // Build SportsActivityLocation schema once per venue, including geo when
    // available. Used both as `containsPlace` on the city Place and as the
    // embedded `item` on each ItemList entry.
    const venueSchemas = (city?.venues || []).map((venue) => {
      const lon = venue?.geometry?.coordinates?.[0];
      const lat = venue?.geometry?.coordinates?.[1];
      const venueUrl = `${process.env.APP_URL}${getCourtPageUrl(venue.slug)}`;
      return {
        venue,
        venueUrl,
        schema: generateSportsActivityLocationJsonLd({
          name: venue.title,
          address: venue.addressString,
          image: venue?.images?.[0]?.url || '',
          url: venueUrl,
          ...(typeof lat === 'number' && typeof lon === 'number' && {
            geo: { latitude: lat, longitude: lon },
          }),
        }),
      };
    });

    // Generate JSON-LD schemas
    const cityPlaceJsonLd = generatePlaceJsonLd({
      name: city.name,
      geo: {
        latitude: city.latitude,
        longitude: city.longitude,
      },
      containsPlace: venueSchemas.map((v) => v.schema),
    });

    const cityVenuesItemList = generateItemListJsonLd({
      name: `Tennis Courts in ${getTitleLocationName(city)}`,
      itemListOrder: 'ItemListUnordered',
      items: venueSchemas.map((v) => ({
        name: v.venue.title,
        url: v.venueUrl,
        image: v.venue?.images?.[0]?.url || undefined,
        item: v.schema,
      })),
    });

    const citySportEventJsonLd = (cityContent?.events || []).map((event) => {
      const addressFromVenue = event.venue?.addressString || '';
      const addressFromCity =
        event?.city && event.city?.name && event.city?.countrySubdivision?.name
          ? `${event.city.name}, ${event.city.countrySubdivision.name}`
          : '';
      return generateSportsEventJsonLd({
        name: event?.title || '',
        sport: capitalize(SportsEnum.Tennis),
        description: event?.description || '',
        startDate: event?.startDateTime || '',
        endDate: event?.endDateTime || '',
        image: event?.coverImageUrl
          ? getImageUrl({
              url: event.coverImageUrl,
              path: event.coverImagePath || '',
            })
          : undefined,
        location: {
          name: event?.venue?.title || '',
          url: event?.venue?.slug
            ? `${process.env.APP_URL}${getCourtPageUrl(event.venue.slug)}`
            : '',
          address:
            event.addressString || addressFromVenue || addressFromCity || event.displayLocation || '',
          geo: {
            latitude: event?.geometry?.coordinates[1] || '',
            longitude: event?.geometry?.coordinates[0] || '',
          },
        },
      });
    });

    const breadcrumbs = generateBreadcrumbListJsonLd({
      items: generateCourtsBreadcrumbs({
        cityName: city?.name || '',
        citySlug: city?.slug || '',
        regionName: city?.countrySubdivision?.name || '',
        regionSlug: city?.countrySubdivision?.slug || '',
        countryId: city?.countrySubdivision?.country?.id || '',
        countryName: city?.countrySubdivision?.country?.name || '',
        countrySlug: city?.countrySubdivision?.country?.slug || '',
        sport: SportsEnum.Tennis.toLowerCase(),
      }).jsonLd,
    });

    const cityNameFull = getTitleLocationName(city);
    const cityNameShort = city?.name;

    const faqs = generateFaqs({
      tournaments: cityContent?.events || [],
      stateTournaments,
      city,
      venues: city?.venues || [],
      coaches: cityContent?.coaches || [],
      topCoaches,
      topClinics,
      coachStarterProfiles: cityContent?.coachStarterProfiles || [],
      groups: cityContent?.groups || [],
      jsonLd: '',
      cityNameFull,
      cityNameShort,
      sport: SportsEnum.Tennis.toLowerCase(),
    } as any);
    const faqJsonLd = generateFaqJsonLd({ items: faqs });

    const combinedSchemas = combineJsonLdSchemas([
      breadcrumbs,
      faqJsonLd,
      citySportEventJsonLd,
      cityPlaceJsonLd,
      cityVenuesItemList,
    ]);

    const { metaTitle, metaDescription } = generateCourtsMeta({
      city,
      sport: SportsEnum.Tennis.toLowerCase(),
    });

    const existingVenues = new Set();
    city.venues.forEach((venue) => {
      existingVenues.add(venue.id);
    });

    const freeVenues = filterVenuesForTagFree(city.venues || []);
    const hasFreeVenues = freeVenues.length > 0;
    const indoorVenues = filterVenuesForTagIndoor(city.venues || []);
    const hasIndoorVenues = indoorVenues.length > 0;
    const outdoorVenues = filterVenuesForTagOutdoor(city.venues || []);
    const hasOutdoorVenues = outdoorVenues.length > 0;

    const availableTags: TagLinkKey[] = [];

    if (hasFreeVenues) {
      availableTags.push(FREE_COURT_SLUG);
    }
    if (hasIndoorVenues) {
      availableTags.push(INDOOR_COURT_SLUG);
    }
    if (hasOutdoorVenues) {
      availableTags.push(OUTDOOR_COURT_SLUG);
    }

    return {
      props: {
        idOrSlug: citySlug,
        city,
        jsonLd: JSON.stringify(combinedSchemas),
        metaTitle,
        metaDescription,
        availableTags,
        sport: SportsEnum.Tennis.toLowerCase(),
        tournaments: cityContent?.events || [],
        stateTournaments,
        coaches: cityContent?.coaches || [],
        topCoaches,
        topClinics,
        coachStarterProfiles: cityContent?.coachStarterProfiles || [],
        groups: cityContent?.groups || [],
        nearbyVenues: (nearbyVenuesResponse?.venues || [])
          .filter((venue) => !existingVenues.has(venue.id))
          .map(({ city: venueCity, ...venue }) => {
            const distance = calculateHaversineDistance({
              coord1: {
                latitude: venue.latitude,
                longitude: venue.longitude,
              },
              coord2: {
                latitude: city.latitude,
                longitude: city.longitude,
              },
            });
            return {
              ...venue,
              distance,
            };
          })
          .sort((a, b) => {
            return a.distance - b.distance;
          }),
      },
      revalidate: REVALIDATE_IN_SECONDS,
    };
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
    return {
      notFound: true,
      revalidate: REVALIDATE_IN_SECONDS,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (IS_ALL_AS_ISR) {
    return {
      paths: [],
      fallback: true,
    };
  }

  const cities = await getAllCityVenuePaths();

  const paths = cities.cities
    .filter((c) => !!c && c.venues.length > 0)
    .map((city) => {
      return {
        params: {
          citySlug: city.slug,
        },
      };
    });

  return {
    paths,
    fallback: true,
  };
};

const CityVenuesPageWithProvider = (props: Props) => {
  return (
    <CityLocationProvider
      city={{
        ...props.city,
        state: props?.city?.countrySubdivision?.name,
        country: props?.city?.countrySubdivision?.country?.id,
      }}
    >
      <CityVenuesPage {...props} />
    </CityLocationProvider>
  );
};

export default CityVenuesPageWithProvider;
